import {
  Button,
  ButtonGroup,
  Card,
  Heading,
  Text,
  Flex,
  Icon,
  Pill,
} from '@/_components';
import { useContext, useState, useMemo, type FC } from 'react';
import {
  getUnsignedFormCount,
  getUnmailedPaperFiledReturnCount,
  getUnpaidPaymentVouchersCount,
} from '@/_utils/taxReturns';
import type { EnhancedTaxReturnDeliveryCardProps } from './EnhancedTaxReturnDeliveryCard.types';
import Link from 'next/link';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { DraftTaxReturnReviewDialog } from '../TaxReturnDeliveryCard/_components/DraftTaxReturnReviewDialog';
import { ClientK1DistributionDialog } from '../TaxReturnDeliveryCard/_components/ClientK1DistributionDialog';
import { createTaxReturnCheckoutSession } from '@/_api/admin-tax-returns';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { isK1PackageSentOrDelivered } from '@/_types/models/taxReturns';
import styles from './EnhancedTaxReturnDeliveryCard.module.css';

export const EnhancedTaxReturnDeliveryCard: FC<
  EnhancedTaxReturnDeliveryCardProps
> = ({ taxReturn, onTaxReturnUpdate, k1DistributionId }) => {
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showK1DistributionDialog, setShowK1DistributionDialog] = useState(
    k1DistributionId === taxReturn.id.toString(),
  );

  const { setSelectedTaxReturn } = useContext(PreviewContext);
  const { user, token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);

  const signatureCount: number =
    taxReturn.status === 'finished'
      ? 0
      : getUnsignedFormCount(taxReturn, user?.id);

  const paperFiledReturnCount = getUnmailedPaperFiledReturnCount(taxReturn);
  const paymentVouchersCount = getUnpaidPaymentVouchersCount(taxReturn);

  const k1PackagesCount = taxReturn.k1_packages?.length || 0;
  const k1PackagesSentOrDeliveredCount =
    taxReturn.k1_packages?.filter(isK1PackageSentOrDelivered).length || 0;
  const k1PackagesNotSentOrDeliveredCount =
    taxReturn.k1_packages?.filter((pkg) => !isK1PackageSentOrDelivered(pkg))
      .length || 0;

  const createCheckoutSession = async () => {
    if (!taxReturn || !token || typeof window === 'undefined') {
      return;
    }
    try {
      const response = await createTaxReturnCheckoutSession({
        id: taxReturn.id,
        companyId: taxReturn.company_id,
        token,
      });

      if (response?.checkout_url) {
        window.location.href = response.checkout_url; // Redirect to the checkout URL
      } else {
        console.error('Checkout URL not found in the response');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  const {
    id,
    company_id: companyId,
    draft_status,
    tax_return_name: name,
  } = taxReturn;

  const isDraft: boolean = useMemo(
    () => draft_status === 'draft',
    [draft_status],
  );
  const isApproved: boolean = useMemo(
    () => draft_status === 'approved',
    [draft_status],
  );

  const isChangeRequested: boolean = useMemo(
    () => draft_status === 'change_requested',
    [draft_status],
  );

  const isClientReviewed = useMemo(
    () => isApproved || isChangeRequested,
    [isApproved, isChangeRequested],
  );

  const k1PackageApplicable = useMemo(
    () =>
      taxReturn.k1_distribution_status !== 'not_applicable' &&
      preferences.tax_return_k1_distribution_enabled === true,
    [
      taxReturn.k1_distribution_status,
      preferences.tax_return_k1_distribution_enabled,
    ],
  );

  const allSigned = useMemo(
    () => taxReturn.all_signed === true,
    [taxReturn.all_signed],
  );

  if (!taxReturn) return null;

  const previewDraftTaxReturn = async () => {
    setSelectedTaxReturn(taxReturn);
    setShowReviewDialog(true);
  };

  const handleReviewDialogClose = () => {
    setShowReviewDialog(false);
    onTaxReturnUpdate();
  };

  const handleK1DistributionDialogClose = () => {
    setShowK1DistributionDialog(false);
    onTaxReturnUpdate();
  };

  const statusMessage = isApproved
    ? "You have approved the return. At this stage, no further edits can be made. Please wait until we have reviewed the return. You will be notified when it's ready."
    : isChangeRequested
      ? "You have requested changes to the return. At this stage, no further edits can be made. You will be notified when it's ready for your signature."
      : isDraft
        ? 'Your tax return has been drafted. Please review the tax return and approve or request changes.'
        : 'Your tax return is ready to review. Please click the button below.';

  const getCardStatus = () => {
    if (isClientReviewed) return 'completed';
    if (isDraft) return 'draft';
    return 'ready';
  };

  const getStatusPills = () => {
    const pills = [];

    // Tax Return type pill
    pills.push(
      <Pill
        key="tax-return"
        iconLeft="scroll"
        label="Tax Return"
        intent="default"
      />,
    );

    // Status pill
    if (isDraft) {
      pills.push(<Pill key="drafted" label="Drafted" intent="secondary" />);
    }

    return pills;
  };

  return (
    <>
      <Card
        padding={8}
        gap={10}
        className={styles.enhancedCard}
        data-status={getCardStatus()}
      >
        <Flex isYCentered={true} gap={4}>
          {getStatusPills()}
        </Flex>
        <Flex isColumn={true} gap={4}>
          <Heading breakWords={true} level={2} color="primary">
            {name}
          </Heading>
          <Text type="small" color="primary">
            {statusMessage}
          </Text>
        </Flex>
        <Flex isColumn={true} gap={4}>
          {signatureCount > 0 && (
            <Flex isYCentered={true} gap={4}>
              <Icon name="signature" size={20} />
              <Text type="small">
                {signatureCount}{' '}
                {signatureCount === 1 ? 'signature' : 'signatures'} to sign
              </Text>
            </Flex>
          )}
          {paperFiledReturnCount > 0 && (
            <Flex isYCentered={true} gap={4}>
              <Icon name="envelope" size={20} />
              <Text type="small">
                {paperFiledReturnCount} paper-filed{' '}
                {paperFiledReturnCount === 1 ? 'return' : 'returns'} to mail
              </Text>
            </Flex>
          )}
          {paymentVouchersCount > 0 && (
            <Flex isYCentered={true} gap={4}>
              <Icon name="receipt" size={20} />
              <Text type="small">
                {paymentVouchersCount}{' '}
                {paymentVouchersCount === 1
                  ? 'payment voucher'
                  : 'payment vouchers'}{' '}
                remaining
              </Text>
            </Flex>
          )}
          {k1PackageApplicable && (
            <>
              {k1PackagesNotSentOrDeliveredCount > 0 && allSigned && (
                <Flex isYCentered={true} gap={4}>
                  <Icon name="file" size={20} />
                  <Text type="small">
                    {k1PackagesNotSentOrDeliveredCount}{' '}
                    {k1PackagesNotSentOrDeliveredCount === 1
                      ? 'K-1 package'
                      : 'K-1 packages'}{' '}
                    are ready to distribute. Please confirm the partner or
                    member information and distribute them to the partners or
                    members.
                  </Text>
                </Flex>
              )}
              {k1PackagesSentOrDeliveredCount > 0 && allSigned && (
                <Flex isYCentered={true} gap={4}>
                  <Icon name="check-circle" size={20} color="success" />
                  <Text type="small">
                    {k1PackagesSentOrDeliveredCount}{' '}
                    {k1PackagesSentOrDeliveredCount === 1
                      ? 'K-1 package'
                      : 'K-1 packages'}{' '}
                    sent or marked as delivered.
                  </Text>
                </Flex>
              )}
            </>
          )}

          {taxReturn.payment &&
            (taxReturn.payment.status === 'paid' ? (
              <Flex isYCentered={true} gap={4}>
                <Icon name="check" size={20} color="success" />
                <Text type="small" color="success" fontWeight="bold">
                  Your invoice of ${taxReturn.payment.amount / 100} has been
                  paid.
                </Text>
              </Flex>
            ) : taxReturn.payment.status === 'pending' ? (
              <Flex isYCentered={true} gap={4}>
                <Icon name="clock" size={20} color="caution" />
                <Text type="small" color="caution" fontWeight="bold">
                  Your invoice of ${taxReturn.payment.amount / 100} is pending.
                  Please wait for it to be processed.
                </Text>
              </Flex>
            ) : (
              <Flex isYCentered={true} gap={4}>
                <Icon name="warning" size={20} color="critical" />
                <Text type="small" color="critical" fontWeight="bold">
                  An Invoice for ${taxReturn.payment.amount / 100} is due.
                  Please pay as soon as possible to avoid delays in your tax
                  return filing.
                </Text>
              </Flex>
            ))}
        </Flex>
        <ButtonGroup align="right">
          {k1PackageApplicable && k1PackagesCount > 0 && allSigned && (
            <Button
              value="Distribute K-1s"
              intent="secondary"
              iconLeft="send"
              onClick={() => setShowK1DistributionDialog(true)}
            />
          )}
          {taxReturn.payment && taxReturn.payment.status !== 'paid' && (
            <Button
              value="Pay Invoice"
              intent="primary"
              onClick={createCheckoutSession}
            />
          )}
          {isDraft || isClientReviewed ? (
            <Button
              value={isDraft ? 'Review Draft' : 'View'}
              intent="primary"
              iconRight="arrow-right"
              onClick={previewDraftTaxReturn}
            />
          ) : !isClientReviewed ? (
            <Link
              href={`/tax-return-delivery/${id}?companyId=${companyId}`}
              tabIndex={-1}
            >
              <Button value="View" intent="primary" iconRight="arrow-right" />
            </Link>
          ) : null}
        </ButtonGroup>
      </Card>
      {(isDraft || isClientReviewed) && showReviewDialog && (
        <DraftTaxReturnReviewDialog
          isReviewed={isClientReviewed}
          onClose={handleReviewDialogClose}
        />
      )}
      {showK1DistributionDialog && (
        <ClientK1DistributionDialog
          taxReturn={taxReturn}
          isOpen={showK1DistributionDialog}
          onClose={handleK1DistributionDialogClose}
          onTaxReturnUpdate={onTaxReturnUpdate}
        />
      )}
    </>
  );
};
